import Layout from '../../components/Layout';
import GuideCard from '../../components/GuideCard';
import { Link } from 'react-router-dom';
import { ArrowRight, Pill, Heart, Leaf, Scale } from 'lucide-react';

const wellnessGuides = [
  {
    title: "Supplements Guide",
    subtitle: "Popular supplements worth considering for general wellness support.",
    category: "Supplements",
    link: "/wellness/supplements",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/xlwpbzxq_supplements-guide.jpg.png"
  },
  {
    title: "Self-Care Essentials",
    subtitle: "Products and tools for relaxation, stress relief, and daily self-care rituals.",
    category: "Self-Care",
    link: "/wellness/self-care-essentials",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/usvliotd_self-care-essentials.jpg.png"
  },
  {
    title: "Daily Health Picks",
    subtitle: "Practical items for everyday wellbeing and healthy habits.",
    category: "Daily Health",
    link: "/wellness/daily-health-picks",
    imageUrl: "https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/52mj1dpa_daily-health-picks.jpg.png"
  }
];

const browseTopics = [
  { icon: Pill, label: "Supplements", link: "/wellness/supplements" },
  { icon: Heart, label: "Self-Care", link: "/wellness/self-care-essentials" },
  { icon: Leaf, label: "Daily Health", link: "/wellness/daily-health-picks" },
  { icon: Scale, label: "Compare Supplements", link: "/wellness/compare" },
];

const WellnessLanding = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="wellness-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
              Health & Wellness
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1C1C1C] leading-tight mb-6">
              Simple habits for <span className="italic font-normal">everyday wellbeing</span>
            </h1>
            <p className="text-base md:text-lg text-[#374151] leading-relaxed">
              From supplements to self-care tools, our wellness guides help you build sustainable habits that support your overall health and happiness.
            </p>
          </div>
        </div>
      </section>

      {/* Start Here Section */}
      <section data-testid="wellness-start-here" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="p-8 md:p-12 bg-[#F5F5F0] border border-[#E5E5E5]">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <span className="text-xs tracking-widest uppercase font-medium text-[#3A4D39] mb-2 block">Start Simple</span>
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-2">
                  Daily health picks
                </h3>
                <p className="text-[#374151] max-w-xl">
                  Begin with practical items you can use every day. Our daily health guide covers the basics that make a real difference.
                </p>
              </div>
              <Link 
                to="/wellness/daily-health-picks"
                data-testid="wellness-start-cta"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A4D39] text-white text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#2A3D29] flex-shrink-0"
              >
                View Daily Picks
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Browse by Topic */}
      <section data-testid="wellness-browse-topics" className="py-16 md:py-20 bg-[#FDFCF8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-8">
            Browse by Topic
          </h2>
          <div className="flex flex-wrap gap-3">
            {browseTopics.map((topic) => (
              <Link
                key={topic.label}
                to={topic.link}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[#E5E5E5] text-[#374151] text-sm font-medium transition-all duration-300 hover:border-[#3A4D39] hover:text-[#3A4D39]"
              >
                <topic.icon className="w-4 h-4" />
                {topic.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* All Guides */}
      <section data-testid="wellness-all-guides" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1C1C1C] mb-4">
              All Wellness Guides
            </h2>
            <p className="text-[#374151]">
              Explore all our wellness guides for supplements, self-care, and daily health.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {wellnessGuides.map((guide) => (
              <GuideCard key={guide.title} {...guide} />
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default WellnessLanding;
